import { SelectData } from '../components/primitives/Select';
import {
  ProjectSort,
  PROJECT_SORT_VALUES,
  SORT_OPTION_TO_COMPARE_FN,
  FILTER_OPTION_TO_COMPARE_FN,
  ProjectFilter,
  PROJECT_FILTER_VALUES,
} from '../utils/project/project-filter';
import { queryClient } from './Api.context';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export type ProjectsContext = {
  projects: Cornerstone.ProjectResult[];
  noProjects: boolean;
  projectsLoading: boolean;
  deleteLoading: boolean;
  isError: boolean;
  addToCart: (project: Cornerstone.ProjectResult) => void;
  addItemMutation: ReturnType<typeof Cornerstone.useAddItemV2>;
  disableProject: (projectId: number, onSuccess: () => void) => void;
  disableProjectMutation: ReturnType<typeof Cornerstone.useDisableProject>;
  selectedSort: ProjectSort;
  setSelectedSort: (value: ProjectSort) => void;
  sortingOptionsList: SelectData[];
  selectedFilter: ProjectFilter;
  setSelectedFilter: (value: ProjectFilter) => void;
  filterOptionsList: SelectData[];
  handleClearFilters: () => void;
};

export const ProjectsContext = React.createContext<ProjectsContext | null>(
  null,
);

export const useProjectsContext = () => {
  const projectsContext = React.useContext(ProjectsContext);

  if (!projectsContext) {
    throw new Error(
      'useProjectsContext has to be used within <ProjectsContext.Provider>',
    );
  }
  return projectsContext;
};

type ProjectsProviderProps = {
  children: React.ReactNode;
};

export const ProjectsProvider: React.FC<ProjectsProviderProps> = ({
  children,
}) => {
  const {
    data: projectsResponse,
    isLoading: projectsLoading,
    isError,
  } = Cornerstone.useGetProjects({
    query: {
      queryKey: ['projectsList'],
    },
  });
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.project-filter',
  });
  const navigate = useNavigate();

  const addItemMutation = Cornerstone.useAddItemV2({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(['checkoutSummary']);
        navigate('/user/cart');
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to price cart for line item',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const addToCart = React.useCallback(
    (project: Cornerstone.ProjectResult) => {
      const addItemRequest: Cornerstone.CartItemAddRequest = {
        projectId: project.id,
        sku: project.sku,
        quantity: 1,
      };

      addItemMutation.mutate({ data: addItemRequest });
    },
    [addItemMutation],
  );

  const disableProjectMutation = Cornerstone.useDisableProject({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(['projectsList']);
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to delete project',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const disableProject = React.useCallback(
    (projectId: number, onSuccess: () => void) => {
      disableProjectMutation.mutate({ projectId }, { onSuccess });
    },
    [disableProjectMutation],
  );

  // filtering out deleted projects
  const nonDeletedProjects = React.useMemo(() => {
    const filtered = projectsResponse?.data.filter(
      (project) => !project.disabled,
    );
    return filtered ? filtered : [];
  }, [projectsResponse?.data]);

  const [selectedSort, setSelectedSort] = React.useState<ProjectSort>(
    'last_uploaded' satisfies ProjectSort,
  );

  const sortingOptionsList = useMemo(
    () =>
      PROJECT_SORT_VALUES.map((sortValue: ProjectSort) => ({
        value: sortValue,
        label: t(sortValue),
      })),
    [t],
  );

  const [selectedFilter, setSelectedFilter] = React.useState<ProjectFilter>(
    'all' satisfies ProjectFilter,
  );

  const filterOptionsList = useMemo(
    () =>
      PROJECT_FILTER_VALUES.map((filterValue: ProjectFilter) => ({
        value: filterValue,
        label: t(filterValue),
      })),
    [t],
  );

  const processedProjects = useMemo(() => {
    const filtered = nonDeletedProjects.filter(
      FILTER_OPTION_TO_COMPARE_FN[selectedFilter] || (() => 0),
    );
    return filtered.sort(SORT_OPTION_TO_COMPARE_FN[selectedSort] || (() => 0));
  }, [nonDeletedProjects, selectedFilter, selectedSort]);

  const handleClearFilters = React.useCallback(() => {
    setSelectedSort('last_uploaded');
    setSelectedFilter('all');
  }, [setSelectedSort, setSelectedFilter]);

  return (
    <ProjectsContext.Provider
      value={{
        projects: processedProjects,
        noProjects: nonDeletedProjects.length === 0,
        projectsLoading,
        deleteLoading: disableProjectMutation.isLoading,
        isError,
        addToCart,
        addItemMutation,
        disableProject,
        disableProjectMutation,
        selectedSort,
        sortingOptionsList,
        setSelectedSort,
        selectedFilter,
        filterOptionsList,
        setSelectedFilter,
        handleClearFilters,
      }}
    >
      {children}
    </ProjectsContext.Provider>
  );
};
